import { Typography } from 'antd';
import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { LOGOUT_MUTATION, USER_QUERY } from '../app/graphql/authQueries';
import { extractGraphqlEntity } from '../common/utils/graphqlUtils';
import routes from '../app/routes';
import IconButton from '../components/buttons/IconButton';
import { GraphQLMutatingLink } from '../components/data/asyncActionElements';
import { DropdownMenu } from '../components/nav/navElements';
import { useNavElementsStyles } from '../components/nav/navElements.styles';
import { useResponsiveQueries } from '../components/responsive/responsiveQueries';
import { InlineLink } from '../components/typography';
import { useLogoutHandler } from '../hooks/data/auth';
import { ReportsDialog } from '../dialogs/ReportsDialog';
import { useReduxDialogControls } from '../common/utils/dialogUtils';
import UserAddressBook from '../containers/UserAddressBook';
import SwitchUserDialog from '../dialogs/SwitchUserDialog';
import InvoiceLookupDrawer from '../dialogs/invoice/InvoiceBookDrawer';
import StatementDrawer from '../dialogs/statement/StatementDrawer';
import UserTemplateManagement from '../components/popups/templates/UserTemplateManagement';
import UserSettingsDialog from './UserSettingsDialog';
import { formatUser } from '../common/utils/formatUtils';
import { FAIcon } from '../components/adapters/fontAwesomeAdapters';
import { useAccountDropdownStyles } from './AccountDropdown.styles';

const ALIGN_ICON = {
  points: ['tr', 'br'],
  offset: [5, 6],
  overflow: {
    adjustX: 0,
    adjustY: 1,
  },
};

function UserLabel({ user, isDesktop }) {
  const { styles } = useAccountDropdownStyles();
  return (
    <div
      className={styles.userLabel}
      data-subject="account"
      data-role="user-label"
    >
      <IconButton icon="user-circle icon-21" />
      {isDesktop && (
        <span className={styles.accountUserName}>
          <span className={styles.accountUserNameEllipsis}>
            {formatUser(user)}
          </span>
          {user?.isAdmin && (
            <>
              |
              <span className={styles.accountAdmin}>
                <FormattedMessage id="account.user.admin" />
              </span>
            </>
          )}
          <FAIcon className="icon-16" icon="caret-down" />
        </span>
      )}
    </div>
  );
}

export function AccountDropdown({ user }) {
  const { onLogoutSuccess, onLogoutError } = useLogoutHandler();
  const [isSwitchUserOpen, setSwitchUserOpen] = useState(false);
  const media = useResponsiveQueries();
  const isSm = media.sm || media.xs;
  const isMd = media.md;
  const isDesktop = media.lg || media.xl || media.xxl;
  const { styles: navStyles } = useNavElementsStyles();
  const userPreferencesControls = useReduxDialogControls({
    id: 'account.userPreferences',
  });

  const items = [
    !isDesktop && {
      key: 'user-name',
      label: (
        <Typography.Text ellipsis style={{ maxWidth: 125, color: '#ea002a' }}>
          {formatUser(user)}
        </Typography.Text>
      ),
    },
    user?.isAdmin && {
      key: 'admin',
      label: (
        <Link to={routes.admin.prefix}>
          <FormattedMessage id="account.admin" />
        </Link>
      ),
    },
    user?.switchAllowed && {
      key: 'switchUser',
      label: (
        <InlineLink onClick={() => setSwitchUserOpen(true)}>
          <FormattedMessage id="account.switchUser" />
        </InlineLink>
      ),
    },
    user && {
      key: 'user-preferences',
      label: (
        <InlineLink onClick={userPreferencesControls.open}>
          <FormattedMessage id="account.userPreferences" />
        </InlineLink>
      ),
    },
    {
      key: 'logout',
      label: (
        <GraphQLMutatingLink
          mutation={LOGOUT_MUTATION}
          onDone={onLogoutSuccess}
          onError={onLogoutError}
          textId="account.logout"
        />
      ),
    },
  ].filter(Boolean);

  return (
    <>
      <DropdownMenu
        items={items}
        overlayClassName={classNames({ [navStyles.iconTrigger]: isSm })}
        label={<UserLabel isDesktop={isDesktop} user={user} />}
        noArrow
        trigger={isSm || isMd ? 'click' : 'hover'}
        align={ALIGN_ICON}
      />
      <SwitchUserDialog
        open={isSwitchUserOpen}
        close={() => setSwitchUserOpen(false)}
      />
    </>
  );
}

export function AccountDropdownDialogs() {
  const { data, loading } = useQuery(USER_QUERY);
  const user = data && extractGraphqlEntity(data);
  const addressBookControls = useReduxDialogControls({
    id: 'account.addressBook',
  });
  const invoiceControls = useReduxDialogControls({
    id: 'account.invoice',
  });
  const statementControls = useReduxDialogControls({
    id: 'account.statement',
  });
  const templatesControls = useReduxDialogControls({
    id: 'account.templates',
  });
  const userPreferencesControls = useReduxDialogControls({
    id: 'account.userPreferences',
  });
  const reportsControls = useReduxDialogControls({
    id: 'account.reports',
  });

  if (loading || !user) {
    return null;
  }

  return (
    <>
      <UserAddressBook
        open={addressBookControls.isOpen}
        openDrawer={addressBookControls.open}
        onClose={addressBookControls.close}
        data={addressBookControls.data}
        setData={addressBookControls.setData}
      />
      <InvoiceLookupDrawer
        onClose={invoiceControls.close}
        open={invoiceControls.isOpen}
      />
      <StatementDrawer
        onClose={statementControls.close}
        open={statementControls.isOpen}
      />
      <UserTemplateManagement
        onClose={templatesControls.close}
        open={templatesControls.isOpen}
      />
      <UserSettingsDialog
        user={user}
        open={userPreferencesControls.isOpen}
        onClose={userPreferencesControls.close}
      />
      <ReportsDialog
        open={reportsControls.isOpen}
        close={reportsControls.close}
      />
    </>
  );
}
